import type { SolrParams } from '#shared/solr'
import type { InferOutput } from 'valibot'
import type { BaseBody } from '~/utils/solr/body'
import type { FacetCounts } from '~/utils/solr/facet'
import { keepPreviousData } from '@tanstack/vue-query'
import { array, flatten, intersect, nullish, object, parse, pipe, safeParse, transform, unknown } from 'valibot'
import { formatValibotError } from '~/utils/errors/formatValibotError'
import { academicArticleSectionSchema, lawReferenceSchema } from '~/utils/solr/academic-article'
import { baseBodySchema } from '~/utils/solr/body'
import { bookFootnoteSchema } from '~/utils/solr/book-footnotes'
import { commentarySchema } from '~/utils/solr/commentary'
import { facetCountsSchema } from '~/utils/solr/facet'
import { headersSchema } from '~/utils/solr/headers'
import { solrHighlightingSchema } from '~/utils/solr/highlighting'
import { journalListSchema } from '~/utils/solr/journal'
import { lawSchema } from '~/utils/solr/law'
import { bookAndArticleSchema, chapterSchema, pageSchema } from '~/utils/solr/literature'
import { workspaceBookSchema, workspaceJournalSchema, workspaceLawSchema, workspaceTemplateSchema } from '~/utils/workspace/favorites'

// Supported Solr document types
const docSchemas = {
  law: lawSchema,
  book: bookAndArticleSchema,
  bookFootnote: bookFootnoteSchema,
  chapter: chapterSchema,
  page: pageSchema,
  journal: journalListSchema,
  commentary: commentarySchema,
  workspaceLaw: workspaceLawSchema,
  workspaceBook: workspaceBookSchema,
  workspaceJournal: workspaceJournalSchema,
  workspaceTemplate: workspaceTemplateSchema,
  academicArticleSection: academicArticleSectionSchema,
  lawReference: lawReferenceSchema,
} as const

export type DocType = keyof typeof docSchemas

export type SolrV2Doc<D extends DocType> = InferOutput<(typeof docSchemas)[D]>

// Explicit shape flattens the deep valibot InferOutput so @tanstack/vue-query's
// MaybeRefDeep<> recursion doesn't blow TS depth budget at the useQuery overload.
export interface SolrV2Response<D extends DocType> {
  responseHeader: InferOutput<typeof headersSchema>
  response: BaseBody & {
    docs: SolrV2Doc<D>[]
    journalProfile?: string
  }
  highlighting?: InferOutput<typeof solrHighlightingSchema> | null
  facet_counts?: FacetCounts | null
}

/**
 * Version 2 of the Solr composable: callers specify the document type
 */
export function useSolrQueryV2<
  D extends DocType,
>(
  params: MaybeRef<SolrParams | null>,
  docType: D,
  endpoint: 'search' | 'literature' | 'favorites' | 'journals' | 'footnotes' | 'templates',
  options: { cache?: boolean, keepPreviousData?: boolean } = { cache: true },
) {
  // @tanstack/vue-query@5.100.x wraps query data in `MaybeRefDeep<>` when
  // validating `useQuery` overloads. Our nested `SolrV2Response<D>` types exceed
  // TypeScript’s recursion depth limit, even though runtime behavior is correct.
  //
  // @ts-expect-error TS2769 (`MaybeRefDeep` recursion on nested generic types)
  return useQuery({
    ...solrQueryOptions(params, docType, endpoint, options),
    ...(options.keepPreviousData ? { placeholderData: keepPreviousData } : {}),
  })
}

export function solrQueryOptions<D extends DocType>(
  params: MaybeRef<SolrParams | null>,
  docType: D,
  endpoint: 'search' | 'literature' | 'favorites' | 'journals' | 'footnotes' | 'templates',
  options: { cache?: boolean, keepPreviousData?: boolean } = { cache: true },
) {
  // Forwards cookies during SSR so licensed endpoints don't 401
  const fetch = useRequestFetch()

  // eslint-disable-next-line @tanstack/query/exhaustive-deps -- fetch is useRequestFetch() (stable per-render), not a real dep
  return queryOptions({
    queryKey: ['solr', endpoint, params, docType],

    staleTime: options.cache ? undefined : 0,
    gcTime: options.cache ? undefined : 0,
    enabled: computed(() => {
      const unrefParams = unref(params)
      if (!unrefParams) {
        return false
      }
      const filtered = Object.entries(unrefParams).filter(([, v]) => v != null)
      return filtered.length > 0
    }),
    async queryFn({ signal }): Promise<SolrV2Response<D>> {
      const res = await fetch(`/api/solr/${endpoint}`, {
        query: unref(params) ?? {},
        signal,
      })

      const parsed = parseSolrResult(docType, res)

      if (endpoint === 'journals' && docType === 'journal') {
        const journalProfile = (res as { response?: { journalProfile?: unknown } })?.response?.journalProfile
        if (typeof journalProfile === 'string') {
          return {
            ...parsed,
            response: {
              ...parsed.response,
              journalProfile,
            },
          }
        }
      }

      return parsed
    },
  })
}

export function parseSolrResult<D extends DocType>(
  docType: D,
  res: unknown,
): SolrV2Response<D> {
  const docSchema = docSchemas[docType]

  const docsFieldSchema = pipe(
    array(unknown()),
    transform(arr =>
      arr.map((doc) => {
        const parsed = safeParse(docSchema, doc)
        if (parsed.success) {
          return parsed.output
        }
        console.error(formatValibotError({
          issues: parsed.issues ?? [],
          id: (doc as any).id,
        }, docType), flatten(parsed.issues ?? []))
        return null
      }),
    ),
    transform(it => it.filter((el): el is NonNullable<typeof el> => el !== null)),
  )

  const responseSchema = intersect([
    baseBodySchema,
    object({
      docs: docsFieldSchema,
    }),
  ])

  const schema = object({
    responseHeader: headersSchema,
    response: responseSchema,
    highlighting: nullish(solrHighlightingSchema),
    facet_counts: nullish(facetCountsSchema),
  })

  return parse(schema, res)
}
